// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Finding source maps. This is a discovery problem, not a parsing problem, and BOTH of the obvious
 * approaches individually produce a green report over a real leak:
 *
 *   - Content-only scanning misses `vite build --sourcemap hidden`: the `.map` file is PRESENT on
 *     disk with full `sourcesContent` leaking the secret, and the comment is ABSENT from the `.js`.
 *   - Extension-only globbing misses `--sourcemap inline`: there are ZERO `.map` files in `dist/`
 *     and the entire map is embedded in the `.js`, `sourcesContent` fully recoverable.
 *
 * So leakmap takes the UNION of three paths, and reports an orphan `.map` that nothing references
 * as a HIGHER-severity finding — somebody thought they had hidden it.
 *
 * THE NAIVE REGEX IS A CONFIDENT-WRONG-ANSWER MACHINE. Five measured hazards:
 *
 *   (i)   A planted decoy `const trap = "//# sourceMappingURL=fake-not-real.js.map"` survives
 *         minification, and `/\/\/[#@]\s*sourceMappingURL=(\S+)/` returns THE DECOY.
 *   (ii)  The spec's own body pattern `^[@#]\s*sourceMappingURL=(\S*?)\s*$` returns NULL when the
 *         comment is a BLOCK comment, because the trailing block-comment closer is still on the
 *         line: `\s*` does not trim it, it PREVENTS the match — silently missing every CSS map.
 *   (iii) A UTF-8 BOM before the comment defeats `^` entirely — zero matches.
 *   (iv)  U+2028 and U+2029 ARE line terminators for JS `^`/`$`: measured,
 *         `/^ab$/m.test('ab\u2028cd') === true`, so a decoy inside a string literal preceded by
 *         U+2028 matches an anchored regex.
 *   (v)   CRLF is NOT a trap — `$` in `m` mode matches before CR. (Claim retracted after measuring.)
 *
 * What ships: strip the BOM, split the file OURSELVES on /\r\n|\r|\n/ so the `m` flag is never
 * relied on, detect the comment opener, STRIP A TRAILING BLOCK-COMMENT CLOSER FROM THE BODY FIRST,
 * then match, and take the LAST match in the file.
 *
 * (Two of these bit this very file while it was being written: an earlier draft of the comment you
 * are reading contained a literal U+2028, and another contained a literal block-comment closer that
 * ended the comment thirteen lines early. Both are exactly the class of thing leakmap looks for.)
 */

/** Real Vite emits `data:application/json;charset=utf-8;base64,` — not the `;base64` of the spec. */
export const INLINE_DATA_RE = /^data:application\/json[^,]*;base64,/i;

const BODY_RE = /^[@#]\s*sourceMappingURL=(\S*?)\s*$/;

export interface SourceMappingRef {
  readonly url: string;
  /** 0-based index into the lines we split ourselves — never a regex match index. */
  readonly line: number;
  readonly inline: boolean;
}

/**
 * The last `sourceMappingURL` comment in a file, or null.
 *
 * The LAST one is authoritative: a decoy inside a string literal appears earlier in the file than
 * the real trailing comment a bundler appends.
 */
export function findSourceMappingURL(text: string): SourceMappingRef | null {
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  // Split ourselves. U+2028/U+2029 are line terminators to the `m` flag but are NOT line breaks a
  // bundler would write a comment after, so splitting only on real breaks is what makes the decoy
  // fixture fail to match.
  const lines = src.split(/\r\n|\r|\n/);

  let best: SourceMappingRef | null = null;
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i]!;
    const trimmed = raw.trim();
    let body: string | null = null;
    if (trimmed.startsWith('//')) {
      body = trimmed.slice(2);
    } else if (trimmed.startsWith('/*')) {
      body = trimmed.slice(2);
      // Strip the closer BEFORE matching. This single line is what makes CSS maps findable.
      if (body.endsWith('*/')) body = body.slice(0, -2);
    }
    if (body === null) continue;
    const m = BODY_RE.exec(body.trim());
    if (!m || !m[1]) continue;
    best = { url: m[1], line: i, inline: INLINE_DATA_RE.test(m[1]) };
  }
  return best;
}

const dec = new TextDecoder('utf-8');

/**
 * Decode an inline `data:` sourcemap.
 *
 * `atob` -> binary string LOSES non-ASCII: measured, emoji in `sourcesContent` were destroyed. The
 * only correct path is atob -> Uint8Array of char codes -> TextDecoder.
 *
 * We also tolerate a non-base64 (URL-encoded) data URL, which the spec permits.
 */
export function decodeInlineMap(url: string): string {
  const comma = url.indexOf(',');
  if (comma < 0) throw new Error('data URL has no comma');
  const meta = url.slice(0, comma + 1);
  const payload = url.slice(comma + 1);
  if (!INLINE_DATA_RE.test(meta)) {
    // Not base64 — a percent-encoded data URL.
    return decodeURIComponent(payload);
  }
  const bin = atob(payload);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return dec.decode(bytes);
}

/**
 * Resolve a non-inline `sourceMappingURL` against the asset's own path, WITHIN THE DROPPED TREE
 * ONLY.
 *
 * We never `fetch()` it. Fetching would exfiltrate the path of a user's private build to a third
 * party — the exact thing this tool exists to prevent — and a root-absolute URL like
 * `/assets/x.js.map` resolves against the DEPLOYED origin, not the folder we were given. A map that
 * is referenced but absent from the dropped tree is its own finding class, not something to go and
 * get.
 */
export function resolveMapPath(assetPath: string, url: string): string | null {
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return null; // absolute URL — out of the tree by definition
  const dir = assetPath.includes('/') ? assetPath.slice(0, assetPath.lastIndexOf('/')) : '';
  const joined = url.startsWith('/') ? url.slice(1) : dir ? `${dir}/${url}` : url;

  const parts: string[] = [];
  for (const seg of joined.split('/')) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') {
      if (parts.length === 0) return null; // escapes the tree
      parts.pop();
      continue;
    }
    parts.push(seg);
  }
  return parts.length ? parts.join('/') : null;
}

/** Text asset classes we search. CSS is a live carrier: a real Vite 8 build emitted a secret into
 * `index-*.css` as a custom property `--leak:"sk_live_…"` with no `.css.map` and no comment. */
export const TEXT_EXTENSIONS = new Set([
  'js', 'mjs', 'cjs', 'jsx', 'ts', 'tsx', 'css', 'html', 'htm', 'json', 'txt', 'map',
  'svg', 'webmanifest', 'xml', 'md', 'env', 'sample', 'yml', 'yaml',
]);

export function extensionOf(path: string): string {
  const base = path.slice(path.lastIndexOf('/') + 1);
  const dot = base.lastIndexOf('.');
  return dot <= 0 ? '' : base.slice(dot + 1).toLowerCase();
}

/**
 * Filenames that should never be in a build output, checked BEFORE any byte is searched.
 *
 * THE HIGHEST-SEVERITY REAL LEAK NEEDS NO SEARCH AT ALL: a `.env` copied into `public/` is shipped
 * verbatim by Vite as `dist/.env`. That is a filename check, and it is finding #1.
 */
export function forbiddenFilename(path: string): { id: string; title: string } | null {
  const base = path.slice(path.lastIndexOf('/') + 1);
  if (/^\.env(\..*)?$/.test(base)) {
    return { id: 'env-in-build', title: 'A .env file is inside your build output' };
  }
  if (/\.(pem|key|p12|pfx|jks|keystore)$/i.test(base)) {
    return { id: 'key-file-in-build', title: 'A private key file is inside your build output' };
  }
  if (/^id_(rsa|dsa|ecdsa|ed25519)$/.test(base)) {
    return { id: 'ssh-key-in-build', title: 'An SSH private key is inside your build output' };
  }
  if (path.startsWith('.git/') || path.includes('/.git/')) {
    return { id: 'git-in-build', title: 'A .git directory is inside your build output' };
  }
  if (/\.(sqlite|sqlite3|db)$/i.test(base)) {
    return { id: 'db-in-build', title: 'A database file is inside your build output' };
  }
  return null;
}
